import { BookingCalendarHolidayResponse } from '@nocode/types/bookingCalendar.type';
import {
  ITimeSlot,
  TimeSlotBindingItemType,
  TimeSlotBindingItemTypeFormatted,
  TimeSlotItemDisplay,
  TimeSlotStatus,
} from '@nocode/types/timeSlot.type';
import { get } from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FlatList, Platform } from 'react-native';
import { getJpHoliday } from '../../utils/calendar';
import {
  getActions,
  getItemListClick,
  getValueBinding,
  getValueBindingComponent,
  ONE_DAY,
} from '../shared';

export const RATIO_TEXT_CONTENT = 0.8;
export const MAX_STOCK_DISPLAY = 999;
export const MIN_STOCK_DISPLAY = 0;
const enum CustomAskingTimeType {
  NEXT_DAY = 'nextDay',
  SAME_DAY = 'sameDay',
}
type BindingValue = {
  timeDuration: number | string;
  'customInitializeTime.enabled': boolean;
  'customInitializeTime.hour': string | number;
  'customInitializeTime.minute': string | number;
  'navigation.defDate': string;
  'navigation.maxDate': string;
  'navigation.minDate': string;
  expandedTime: string;
  'customMondayTime.enabled': boolean;
  'customMondayTime.from'?: string;
  'customMondayTime.to'?: string;
  'customTuesdayTime.enabled': boolean;
  'customTuesdayTime.from'?: string;
  'customTuesdayTime.to'?: string;
  'customWednesdayTime.enabled': boolean;
  'customWednesdayTime.from'?: string;
  'customWednesdayTime.to'?: string;
  'customThursdayTime.enabled': boolean;
  'customThursdayTime.from'?: string;
  'customThursdayTime.to'?: string;
  'customFridayTime.enabled': boolean;
  'customFridayTime.from'?: string;
  'customFridayTime.to'?: string;
  'customSaturdayTime.enabled': boolean;
  'customSaturdayTime.from'?: string;
  'customSaturdayTime.to'?: string;
  'customSundayTime.enabled': boolean;
  'customSundayTime.from'?: string;
  'customSundayTime.to'?: string;
  'stockConfig.enabled': string; // boolean
  'stockConfig.maximumStock': string; // number
  'customAskingTimeNextDay.enabled': boolean;
  'customAskingTimeNextDay.time': string; // HH:mm
  'customAskingTimeNextDay.label': string;
  'customAskingTimeSameDay.enabled': boolean;
  'customAskingTimeSameDay.time': string; // HH:mm
  'customAskingTimeSameDay.label': string;
  'latestBookStartTime.enabled': boolean;
  'latestBookStartTime.time'?: string; // HH:mm
};

const parseMinuteToTimeLabel = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const minute = minutes - hours * 60;
  return `${hours.toString().padStart(2, '0')}:${minute
    .toString()
    .padStart(2, '0')}`;
};

/**
 * 01 -> {hour: 1, minute: 0}
 *
 * 00:00 -> {hour: 0, minute: 0}
 *
 * 00:30 -> {hour: 0, minute: 30}
 * ...
 * 23:30 -> {hour: 23, minute: 30}
 *
 * invalid -> null
 */
const getHourAndMinuteFromTimeString = (
  timeStr?: string
): {
  hour: number;
  minute: number;
} | null => {
  if (!timeStr) {
    return null;
  }
  if (!timeStr.includes(':')) {
    timeStr = `${timeStr}:00`;
  }
  const [HH, mm] = timeStr.split(':');
  const hour = Number(HH);
  const minute = Number(mm);
  if (Number.isNaN(hour) || hour > 23 || hour < 0) {
    return null;
  }
  if (Number.isNaN(minute) || minute > 60 || minute < 0) {
    return null;
  }
  return {
    hour,
    minute,
  };
};
/**
 * 01 -> 60
 *
 * 00:00 -> 0
 *
 * 00:30 -> 30
 * ...
 * 23:30 -> 1410
 *
 * invalid -> null
 */
function getMinuteFromDisplayTime(timeStr: string | undefined): number | null {
  try {
    const hourAndMinute = getHourAndMinuteFromTimeString(timeStr);
    if (!hourAndMinute) {
      return null;
    }
    return hourAndMinute.hour * 60 + hourAndMinute.minute;
  } catch (_) {
    return null;
  }
}
/**
 * 0 -> [0,0]
 * 60 -> [1,0]
 * 1410 -> [23,30]
 * 1440 -> [24,0]
 */
const getTimeFromMinute = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const minute = minutes - hours * 60;
  return [hours, minute];
};
const DEFAULT_MIN_FROM = getMinuteFromDisplayTime('09:00')!;
const DEFAULT_MAX_TO = getMinuteFromDisplayTime('19:00')!;
const MAXIMUM_MINUTE_IN_DAY = 1440;
const MINIMUM_MINUTE_IN_DAY = 0;

/**
 * @param {string} timeString hh:mm
 * @param {number} durationMinute 30
 */
const getIndexFromTimeString = ({
  durationMinute,
  timeString,
  displayTime,
}: {
  timeString: string;
  durationMinute: number;
  displayTime: { from: number };
}) => {
  const minutes = getMinuteFromDisplayTime(timeString) || 0;
  return Math.floor((minutes - displayTime.from) / durationMinute);
};

const getMockData = (): TimeSlotBindingItemType[] => {
  const currentTime = moment();
  return [
    {
      id: `1`,
      blockStartTime: currentTime
        .hours(10)
        .minutes(0)
        .add(1, 'day')
        .toISOString(),
      blockEndTime: currentTime
        .hours(11)
        .minutes(0)
        .add(1, 'day')
        .toISOString(),
      'stockConfig.stockUsedCount': '2',
      createdAt: currentTime.toISOString(),
      updatedAt: currentTime.toISOString(),
    },
    {
      id: `2`,
      blockStartTime: currentTime
        .hours(14)
        .minutes(0)
        .add(2, 'day')
        .toISOString(),
      blockEndTime: currentTime
        .hours(14)
        .minutes(30)
        .add(2, 'day')
        .toISOString(),
      'stockConfig.stockUsedCount': '1',
      createdAt: currentTime.toISOString(),
      updatedAt: currentTime.toISOString(),
    },
  ];
};

const getTimeBlockStatus = ({
  eventStartTime,
  expandedTime,
  timeBlockData,
  stockConfig,
  displayTime,
}: {
  eventStartTime: Date;
  expandedTime: number; // minutes
  timeBlockData: TimeSlotBindingItemTypeFormatted[];
  stockConfig: {
    enabled: boolean;
    maximumStock: number;
  };
  displayTime: {
    from: number;
    to: number;
  };
}): Pick<TimeSlotItemDisplay, 'status' | 'availableStock'> => {
  const eventEndTime = moment(eventStartTime).add(expandedTime, 'minutes');
  // return BLOCKED if eventEndTime is not in the same day as eventStartTime
  if (eventEndTime.date() !== eventStartTime.getDate()) {
    return {
      status: TimeSlotStatus.BLOCKED,
      availableStock: 0,
    };
  }
  const isEventBeforeDisplayTime =
    getMinuteFromDisplayTime(moment(eventStartTime).format('HH:mm'))! <
    displayTime.from;
  const isEventAfterDisplayTime =
    getMinuteFromDisplayTime(moment(eventEndTime).format('HH:mm'))! >
    displayTime.to;
  if (isEventBeforeDisplayTime || isEventAfterDisplayTime) {
    return {
      status: TimeSlotStatus.BLOCKED,
      availableStock: 0,
    };
  }
  if (stockConfig.enabled) {
    const result: Pick<TimeSlotItemDisplay, 'status' | 'availableStock'> = {
      status: TimeSlotStatus.AVAILABLE,
      availableStock: stockConfig.maximumStock,
    };
    for (const {
      blockEndTime,
      blockStartTime,
      stockUsedCount,
    } of timeBlockData) {
      if (
        moment(eventStartTime).isBetween(
          blockStartTime,
          blockEndTime,
          null,
          '()'
        ) ||
        moment(blockStartTime).isBetween(
          eventStartTime,
          eventEndTime,
          null,
          '[)'
        )
      ) {
        result.availableStock = result.availableStock - stockUsedCount;
      }
    }
    if (result.availableStock <= 0) {
      result.availableStock = 0;
      result.status = TimeSlotStatus.BLOCKED;
    }
    return result;
  }
  // when stockConfig is disabled
  for (const { blockEndTime, blockStartTime } of timeBlockData) {
    // blockStartTime -> eventStartTime -> blockEndTime
    if (
      moment(eventStartTime).isBetween(blockStartTime, blockEndTime, null, '()')
    ) {
      return {
        availableStock: 0,
        status: TimeSlotStatus.BLOCKED,
      };
    }
    // eventStartTime -> blockStartTime -> blockEndTime -> eventEndTime
    if (
      moment(blockStartTime).isBetween(eventStartTime, eventEndTime, null, '[)')
    ) {
      return {
        availableStock: 0,
        status: TimeSlotStatus.BLOCKED,
      };
    }
  }
  // return default value
  return {
    status: TimeSlotStatus.AVAILABLE,
    availableStock: 1,
  };
};
export const useTimeSlot = (props: ITimeSlot) => {
  const [beginDate, setBeginDate] = useState(moment().startOf('day').toDate());
  const [initializeTimeIndex, setInitializeTimeIndex] = useState(1);
  const [holidays, setHolidays] = useState<BookingCalendarHolidayResponse>();
  const leftRef = useRef<FlatList<string>>(null);
  const rightRef = useRef<FlatList<string>>(null);
  const { onPress, dataBinding = [], attributes, changeInput } = props;
  const {
    timeDuration = '30',
    initializeList,
    dayViewNumber = '7days',
    styles: customStyles,
  } = attributes;
  const isCanvas =
    Platform.OS === 'web' &&
    !queryString.parse(window?.location?.search)?.target;
  const durationMinute = +timeDuration;

  const bindingValue = useMemo(
    () =>
      getValueBinding(
        props.id,
        dataBinding?.[0] || props.componentData || {},
        props
      ) as BindingValue,
    [props]
  );

  const stockConfig = useMemo(() => {
    return {
      enabled: get(bindingValue, 'stockConfig.enabled')?.toString() === 'true',
      maximumStock: Number(get(bindingValue, 'stockConfig.maximumStock')) || 0,
    };
  }, [bindingValue]);

  const latestBookStartTime = useMemo(() => {
    return {
      enabled:
        get(bindingValue, 'latestBookStartTime.enabled')?.toString() === 'true',
      time: get(bindingValue, 'latestBookStartTime.time', ''),
    };
  }, [bindingValue]);

  const customAskingTimeSameDay = useMemo(() => {
    return {
      enabled:
        get(bindingValue, 'customAskingTimeSameDay.enabled')?.toString() ===
        'true',
      time: get(bindingValue, 'customAskingTimeSameDay.time', ''),
    };
  }, [bindingValue]);
  const customAskingTimeNextDay = useMemo(() => {
    return {
      enabled:
        get(bindingValue, 'customAskingTimeNextDay.enabled')?.toString() ===
        'true',
      time: get(bindingValue, 'customAskingTimeNextDay.time', ''),
    };
  }, [bindingValue]);

  const getCustomDisplayTimeRange = (): { from: number; to: number } => {
    const minFrom = Math.min(
      get(bindingValue, 'customMondayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customMondayTime.from']) ??
            DEFAULT_MIN_FROM
        : MAXIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customTuesdayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customTuesdayTime.from']) ??
            DEFAULT_MIN_FROM
        : MAXIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customWednesdayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customWednesdayTime.from']) ??
            DEFAULT_MIN_FROM
        : MAXIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customThursdayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customThursdayTime.from']) ??
            DEFAULT_MIN_FROM
        : MAXIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customFridayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customFridayTime.from']) ??
            DEFAULT_MIN_FROM
        : MAXIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customSaturdayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customSaturdayTime.from']) ??
            DEFAULT_MIN_FROM
        : MAXIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customSundayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customSundayTime.from']) ??
            DEFAULT_MIN_FROM
        : MAXIMUM_MINUTE_IN_DAY
    );
    const maxTo = Math.max(
      get(bindingValue, 'customMondayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customMondayTime.to']) ??
            DEFAULT_MAX_TO
        : MINIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customTuesdayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customTuesdayTime.to']) ??
            DEFAULT_MAX_TO
        : MINIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customWednesdayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customWednesdayTime.to']) ??
            DEFAULT_MAX_TO
        : MINIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customThursdayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customThursdayTime.to']) ??
            DEFAULT_MAX_TO
        : MINIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customFridayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customFridayTime.to']) ??
            DEFAULT_MAX_TO
        : MINIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customSaturdayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customSaturdayTime.to']) ??
            DEFAULT_MAX_TO
        : MINIMUM_MINUTE_IN_DAY,
      get(bindingValue, 'customSundayTime.enabled')
        ? getMinuteFromDisplayTime(bindingValue['customSundayTime.to']) ??
            DEFAULT_MAX_TO
        : MINIMUM_MINUTE_IN_DAY
    );
    return {
      from: minFrom,
      to: maxTo,
    };
  };

  /**
   *
   * @param day 1: Monday, 2: Tuesday, 3: Wednesday, 4: Thursday, 5: Friday, 6: Saturday, 0: Sunday
   */
  const getCustomDisplayTimeRangeByDay = (
    day: number
  ): { from: number; to: number } | null => {
    switch (day) {
      case 1:
        return get(bindingValue, 'customMondayTime.enabled')
          ? {
              from:
                getMinuteFromDisplayTime(
                  bindingValue['customMondayTime.from']
                ) ?? DEFAULT_MIN_FROM,
              to:
                getMinuteFromDisplayTime(bindingValue['customMondayTime.to']) ??
                DEFAULT_MAX_TO,
            }
          : null;
      case 2:
        return get(bindingValue, 'customTuesdayTime.enabled')
          ? {
              from:
                getMinuteFromDisplayTime(
                  bindingValue['customTuesdayTime.from']
                ) ?? DEFAULT_MIN_FROM,
              to:
                getMinuteFromDisplayTime(
                  bindingValue['customTuesdayTime.to']
                ) ?? DEFAULT_MAX_TO,
            }
          : null;
      case 3:
        return get(bindingValue, 'customWednesdayTime.enabled')
          ? {
              from:
                getMinuteFromDisplayTime(
                  bindingValue['customWednesdayTime.from']
                ) ?? DEFAULT_MIN_FROM,
              to:
                getMinuteFromDisplayTime(
                  bindingValue['customWednesdayTime.to']
                ) ?? DEFAULT_MAX_TO,
            }
          : null;
      case 4:
        return get(bindingValue, 'customThursdayTime.enabled')
          ? {
              from:
                getMinuteFromDisplayTime(
                  bindingValue['customThursdayTime.from']
                ) ?? DEFAULT_MIN_FROM,
              to:
                getMinuteFromDisplayTime(
                  bindingValue['customThursdayTime.to']
                ) ?? DEFAULT_MAX_TO,
            }
          : null;
      case 5:
        return get(bindingValue, 'customFridayTime.enabled')
          ? {
              from:
                getMinuteFromDisplayTime(
                  bindingValue['customFridayTime.from']
                ) ?? DEFAULT_MIN_FROM,
              to:
                getMinuteFromDisplayTime(bindingValue['customFridayTime.to']) ??
                DEFAULT_MAX_TO,
            }
          : null;
      case 6:
        return get(bindingValue, 'customSaturdayTime.enabled')
          ? {
              from:
                getMinuteFromDisplayTime(
                  bindingValue['customSaturdayTime.from']
                ) ?? DEFAULT_MIN_FROM,
              to:
                getMinuteFromDisplayTime(
                  bindingValue['customSaturdayTime.to']
                ) ?? DEFAULT_MAX_TO,
            }
          : null;
      case 0:
        return get(bindingValue, 'customSundayTime.enabled')
          ? {
              from:
                getMinuteFromDisplayTime(
                  bindingValue['customSundayTime.from']
                ) ?? DEFAULT_MIN_FROM,
              to:
                getMinuteFromDisplayTime(bindingValue['customSundayTime.to']) ??
                DEFAULT_MAX_TO,
            }
          : null;
    }
    return {
      from: DEFAULT_MIN_FROM,
      to: DEFAULT_MAX_TO,
    };
  };

  const displayTime = getCustomDisplayTimeRange();
  const getInitValues = (): TimeSlotBindingItemType[] => {
    if (Platform.OS !== 'web') {
      // App
      return dataBinding;
    }
    // web
    if (isCanvas) {
      // return mock data on canvas
      return getMockData();
    }
    // preview
    return dataBinding;
  };

  const data: TimeSlotBindingItemTypeFormatted[] = getInitValues()
    .filter((e) => moment(e.blockStartTime).isValid())
    .map((item) => ({
      createdAt: new Date(item.createdAt),
      blockStartTime: new Date(item.blockStartTime),
      blockEndTime: new Date(item.blockEndTime),
      stockUsedCount: Number(item['stockConfig.stockUsedCount']) || 0,
      id: item.id,
      updatedAt: new Date(item.updatedAt),
    }))
    .sort((a, b) => moment(a.blockStartTime).diff(b.blockStartTime));

  const numberOfDaysDisplayed = useMemo(() => {
    switch (dayViewNumber) {
      case '3days':
        return 3;
      case '4days':
        return 4;
      case '5days':
        return 5;
      case '7days':
        return 7;
      case '1month':
        if (isCanvas && !customStyles.enabled) {
          return 7;
        }
        return moment(beginDate).daysInMonth();
    }
  }, [dayViewNumber, beginDate]);

  const getDateFromFormula = (name: 'defDate' | 'maxDate' | 'minDate') => {
    const bindingId = get(
      attributes,
      `navigation.${name}.parsedFormula.source.objectId`
    );
    return get(props.dependencies, `valueInputs.${bindingId}`);
  };

  const getNavigationDate = (name: 'defDate' | 'maxDate' | 'minDate') => {
    const date = get(bindingValue, `navigation.${name}`);
    if (moment(date).isValid()) {
      return moment(date).toDate();
    }
    if (Number(date)) {
      return new Date(ONE_DAY * Number(date));
    }
    const dateFormula = getDateFromFormula(name);
    if (moment(dateFormula).isValid()) {
      return moment(dateFormula).toDate();
    }
    return null;
  };

  const navigation = useMemo(() => {
    return {
      defDate: getNavigationDate('defDate'),
      maxDate: getNavigationDate('maxDate'),
      minDate: getNavigationDate('minDate'),
    };
    // this value is depend on bindingValue, so it will be re-render when bindingValue change
  }, [bindingValue]);

  const itemDisplay: TimeSlotItemDisplay[] = useMemo(() => {
    if (durationMinute <= 0) {
      return [];
    }
    const currentTime = moment();
    const _beginDate = moment(beginDate)
      .startOf('day')
      .add(displayTime.from, 'minutes');
    let _endDate = moment(beginDate)
      .add(numberOfDaysDisplayed, 'days')
      .startOf('day')
      .add(displayTime.to, 'minutes');
    if (navigation.maxDate && _endDate.isAfter(navigation.maxDate)) {
      _endDate = moment(navigation.maxDate).startOf('day');
    }
    const expandedTime = Number(bindingValue.expandedTime) || 0;
    const [beginHour, beginMinute] = getTimeFromMinute(displayTime.from)!;
    const result: TimeSlotItemDisplay[] = [];
    // get timeBlock status
    for (
      let timeBlock = _beginDate.clone();
      timeBlock.isSameOrBefore(_endDate, 'date');
      timeBlock.add(durationMinute, 'minutes')
    ) {
      if (
        getMinuteFromDisplayTime(timeBlock.format('HH:mm'))! > displayTime.to
      ) {
        timeBlock.set('hour', beginHour);
        timeBlock.set('minute', beginMinute);
        timeBlock.add(1, 'day');
        timeBlock.subtract(durationMinute, 'minutes');
        continue;
      }
      if (
        timeBlock.isBefore(navigation.minDate, 'date') ||
        timeBlock.isAfter(navigation.maxDate, 'date')
      ) {
        result.push({
          availableStock: 0,
          eventStarttime: timeBlock.toDate(),
          status: TimeSlotStatus.BLOCKED,
        });
        continue;
      }
      if (currentTime.isAfter(timeBlock)) {
        result.push({
          availableStock: 0,
          eventStarttime: timeBlock.toDate(),
          status: TimeSlotStatus.BLOCKED,
        });
        continue;
      }
      if (timeBlock.isSame(_endDate, 'date')) {
        if (latestBookStartTime.enabled && latestBookStartTime.time) {
          const minuteFromTimeString = getMinuteFromDisplayTime(
            latestBookStartTime.time
          );
          if (minuteFromTimeString) {
            if (
              getMinuteFromDisplayTime(currentTime.format('HH:mm'))! <
              minuteFromTimeString
            ) {
              result.push({
                availableStock: 0,
                eventStarttime: timeBlock.toDate(),
                status: TimeSlotStatus.BLOCKED,
              });
              continue;
            }
          }
        }
      }
      const displayTimeByDay = getCustomDisplayTimeRangeByDay(timeBlock.day());
      const { availableStock, status } = displayTimeByDay
        ? getTimeBlockStatus({
            displayTime: displayTimeByDay,
            eventStartTime: timeBlock.toDate(),
            expandedTime: expandedTime || durationMinute,
            stockConfig,
            timeBlockData: data,
          })
        : {
            availableStock: 0,
            status: TimeSlotStatus.BLOCKED,
          };
      result.push({
        availableStock,
        eventStarttime: timeBlock.toDate(),
        status,
      });
    }

    // check custom asking time
    const askingDates: Record<string, { hour: number; minute: number }> = {}; // key: date in format YYYY-MM-DD, value: start asking time
    if (customAskingTimeSameDay.enabled && customAskingTimeSameDay.time) {
      const askingTimeHourAndMinute = getHourAndMinuteFromTimeString(
        customAskingTimeSameDay.time
      );
      if (askingTimeHourAndMinute) {
        const startAskingTime = currentTime
          .clone()
          .set('hour', askingTimeHourAndMinute.hour)
          .set('minute', askingTimeHourAndMinute.minute);
        if (moment().isAfter(startAskingTime)) {
          askingDates[startAskingTime.format('YYYY-MM-DD')] =
            askingTimeHourAndMinute;
        }
      }
    }
    if (customAskingTimeNextDay.enabled && customAskingTimeNextDay.time) {
      const askingTimeHourAndMinute = getHourAndMinuteFromTimeString(
        customAskingTimeNextDay.time
      );
      if (askingTimeHourAndMinute) {
        const startAskingTime = currentTime
          .clone()
          .set('hour', askingTimeHourAndMinute.hour)
          .set('minute', askingTimeHourAndMinute.minute);
        if (moment().isAfter(startAskingTime)) {
          askingDates[
            startAskingTime.clone().add(1, 'days').format('YYYY-MM-DD')
          ] = { hour: 0, minute: 0 };
        }
      }
    }
    // update status of timeslot when asking time is reached
    if (Object.keys(askingDates).length > 0) {
      return result.map((item) => {
        const currentTimeDateFormatted = currentTime
          .clone()
          .format('YYYY-MM-DD');
        const eventStartTimeDateFormatted = moment(item.eventStarttime).format(
          'YYYY-MM-DD'
        );
        const askingTime = askingDates[eventStartTimeDateFormatted];
        if (item.status === TimeSlotStatus.AVAILABLE && askingTime) {
          const startAskingTime = moment(item.eventStarttime)
            .set('hour', askingTime.hour)
            .set('minute', askingTime.minute);
          if (moment(item.eventStarttime).isAfter(startAskingTime)) {
            return {
              ...item,
              status:
                currentTimeDateFormatted === eventStartTimeDateFormatted
                  ? TimeSlotStatus.ASKING_SAME_DAY
                  : TimeSlotStatus.ASKING_NEXT_DAY,
            };
          }
        }
        return item;
      });
    }
    return result;
  }, [beginDate, bindingValue, navigation]);

  /**
   * Example:
   [
   '00:00': {'2024-10-23': [TimeSlotItemDisplay], ..., '2024-10-30': [TimeSlotItemDisplay]},
   '00:30': {'2024-10-23': [], ..., '2024-10-30': []},
   ...
   '11:30': {'2024-10-23': [], ..., '2024-10-30': []},
   '12:00': {'2024-10-23': [], ..., '2024-10-30': []},
   '12:30': {'2024-10-23': [], ..., '2024-10-30': []},
   ...
   '23:30': {'2024-10-23': [], ..., '2024-10-30': []},
   ]
   */
  const durationMapping = useMemo(() => {
    const result: Record<string, Record<string, TimeSlotItemDisplay[]>> = {};
    const getInitDurationMappingItem = (): Record<
      string,
      TimeSlotItemDisplay[]
    > =>
      Array(numberOfDaysDisplayed)
        .fill(0)
        .map((_, i) => moment(beginDate).add(i, 'day').format('YYYY-MM-DD'))
        .reduce((prev, curr) => {
          return {
            ...prev,
            [curr]: [],
          };
        }, {});
    if (isCanvas) {
      // render maximum 20 row items on canvas
      const maximumMinute = Math.min(
        20 * durationMinute + displayTime.from,
        displayTime.to
      );
      for (
        let minuteRun = displayTime.from;
        minuteRun < maximumMinute;
        minuteRun += durationMinute
      ) {
        result[parseMinuteToTimeLabel(minuteRun)] =
          getInitDurationMappingItem();
      }
    } else {
      for (
        let minuteRun = displayTime.from;
        minuteRun < displayTime.to;
        minuteRun += durationMinute
      ) {
        result[parseMinuteToTimeLabel(minuteRun)] =
          getInitDurationMappingItem();
      }
    }
    const durationArrayKeys = Object.keys(result);
    itemDisplay.forEach((item) => {
      const timeString = moment(item.eventStarttime).format('HH:mm');
      const index = getIndexFromTimeString({
        timeString,
        durationMinute,
        displayTime,
      });
      const durationArrayKey = durationArrayKeys[index];
      const date = moment(item.eventStarttime).format('YYYY-MM-DD');
      result[durationArrayKey]?.[date]?.push(item);
    });
    return result;
  }, [itemDisplay, numberOfDaysDisplayed]);

  const onNextPage = useCallback(() => {
    setBeginDate((old) => {
      const [value, unit] = (dayViewNumber.match(/\d+|\D+/g) ?? [
        7,
        'days',
      ]) as [number, moment.unitOfTime.Base];

      const nextBeginDate = moment(old).add(value, unit);
      if (
        navigation.maxDate &&
        nextBeginDate.isAfter(navigation.maxDate, 'date')
      ) {
        return old;
      }
      const nextEndDate = nextBeginDate.clone().add(value, unit);
      if (
        navigation.maxDate &&
        nextEndDate.isAfter(navigation.maxDate, 'date')
      ) {
        const nextBeginDate = moment(navigation.maxDate)
          .subtract(value, unit)
          .add(1, 'days')
          .toDate();
        return nextBeginDate;
      }
      return nextBeginDate.toDate();
    });
  }, [dayViewNumber, navigation.maxDate]);

  const onPreviousPage = useCallback(() => {
    setBeginDate((old) => {
      const [value, unit] = (dayViewNumber.match(/\d+|\D+/g) ?? [
        7,
        'days',
      ]) as [number, moment.unitOfTime.Base];

      const nextBeginDate = moment(old).subtract(value, unit);
      const nextEndDate = nextBeginDate.clone().add(value, unit);
      if (
        navigation.minDate &&
        nextEndDate.isBefore(navigation.minDate, 'date')
      ) {
        return old;
      }
      if (
        navigation.minDate &&
        nextBeginDate.isBefore(navigation.minDate, 'date')
      ) {
        return moment(navigation.minDate).toDate();
      }
      return nextBeginDate.toDate();
    });
  }, [dayViewNumber, navigation.minDate]);

  const handleAction = async (id: string, data: any = {}) => {
    const arrayAction = getActions(props, id);
    return await onPress(arrayAction, data);
  };
  const handleSuccessAction = async () => {
    if (attributes?.successActions) {
      await handleAction('successActions', {
        ...attributes?.successActions,
        actionData: {
          itemListClick: getItemListClick(props.record),
        },
      });
    }
  };
  const handleAskingAction = async () => {
    if (attributes?.askingActions) {
      await handleAction('askingActions', {
        ...attributes?.askingActions,
        actionData: {
          itemListClick: getItemListClick(props.record),
        },
      });
    }
  };
  const handleFailureAction = async () => {
    if (attributes?.failedActions) {
      await handleAction('failedActions', {
        ...attributes?.failedActions,
        actionData: {
          itemListClick: getItemListClick(props.record),
        },
      });
    }
  };
  // handle press event
  const handlePress = async (item?: TimeSlotItemDisplay) => {
    if (!item) {
      return;
    }
    changeInput &&
      (await changeInput({
        availableStock: item.availableStock,
        eventStarttime: moment(item.eventStarttime).toDate().getTime(),
      }));
    if (item.status === TimeSlotStatus.AVAILABLE) {
      // run success action
      return handleSuccessAction();
    }
    if (
      item.status === TimeSlotStatus.ASKING_SAME_DAY ||
      item.status === TimeSlotStatus.ASKING_NEXT_DAY
    ) {
      // run asking action
      return handleAskingAction();
    }
    if (item.status === TimeSlotStatus.BLOCKED) {
      // Run failure action
      return handleFailureAction();
    }
  };

  // set begin date
  useEffect(() => {
    if (isCanvas) {
      return;
    }
    const beginDate = getNavigationDate('defDate');
    if (beginDate) {
      setBeginDate(beginDate);
      return;
    }
    // this hook is depend on bindingValue, so it will be re-render when bindingValue chang
  }, [bindingValue]);

  // update initialize time
  useEffect(() => {
    if (isCanvas) {
      return;
    }
    const currentTime = moment();
    let initializeTime = currentTime.format('HH:mm');
    if (bindingValue['customInitializeTime.enabled']) {
      // const hour = get(bindingValue, 'customInitializeTime.hour');

      const hour =
        get(bindingValue, 'customInitializeTime.hour') ||
        getValueBindingComponent(attributes, 'customInitializeTime.hour') ||
        get(attributes, 'customInitializeTime.hour.parsedFormula') ||
        currentTime.format('HH');
      const minute =
        get(bindingValue, 'customInitializeTime.minute') ||
        getValueBindingComponent(attributes, 'customInitializeTime.minute') ||
        get(attributes, 'customInitializeTime.minute.parsedFormula') ||
        currentTime.format('mm');
      const hourStr =
        hour.toString().match(/\d+/) && Number(hour) >= 0 && Number(hour) < 24
          ? Number(hour).toString().padStart(2, '0')
          : currentTime.format('HH');
      const minuteStr =
        minute.toString().match(/\d+/) &&
        Number(minute) >= 0 &&
        Number(minute) <= 60
          ? Number(minute).toString().padStart(2, '0')
          : currentTime.format('mm');
      initializeTime = `${hourStr}:${minuteStr}`;
    }
    const index = getIndexFromTimeString({
      durationMinute,
      timeString: initializeTime,
      displayTime,
    });
    setInitializeTimeIndex(index);
  }, [bindingValue, displayTime]);

  // scroll to current time
  useEffect(() => {
    if (!isCanvas && !initializeList && rightRef?.current && leftRef.current) {
      setTimeout(() => {
        const offset =
          initializeTimeIndex * attributes.lineHeight * RATIO_TEXT_CONTENT;
        leftRef.current?.scrollToOffset({
          animated: false,
          offset,
        });
        // rightRef will be sync with left.
      }, 300);
    }
  }, [initializeList, initializeTimeIndex, rightRef?.current, leftRef.current]);

  // get holidays
  useEffect(() => {
    getJpHoliday().then((r) => setHolidays(r));
  }, []);

  return {
    durationMapping,
    beginDate,
    onPreviousPage,
    onNextPage,
    bindingValue,
    stockConfig,
    handlePress,
    initializeTimeIndex,
    holidays,
    numberOfDaysDisplayed,
    rightRef,
    leftRef,
  };
};
